import React from 'react';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
  KompisSheet,
  KompisText,
  KompisToggle,
  KompisButton,
} from '@inter-ikea-kompis/react-components';
import { useDispatch, useSelector } from 'react-redux';
import { selectShowSceneSheet } from '../../../state/scene/sceneSelectors';
import { actionHideSceneSheet } from '../../../state/scene';
import { actionSetRangeData } from '../../../state/rangeData/rangeDataActions';
import { actionSetFilter } from '../../../state/productMenu/productMenuActions';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ButtonTypeEnum } from '@inter-ikea-kompis/component-button';
import { previousView } from '../../../state/navigation';
import { ITEMS } from '../../../constants';
import styles from './SelectElvarliVariantSheet.module.less';
import { translate } from '../../../services/L10n';
import { t } from '../../../translations';
import { selectIsMobileLandscape } from '../../../state/userAgent/userAgentSelectors';
import { SheetSizeEnum } from '@inter-ikea-kompis/component-sheet';
import { thunkConditionalDisplayIntroPopups } from '../../../state/popups/popupsThunks';
import constants from '../../../settings/constants';
import { thunkRemoveUnwantedSectionSubFilterOptions } from '../../../services/scene/elvarli';
import localStatisticsReporter from '../../../services/statistics/insights/custom/local/localStatisticsReporter';

export const SELECT_ELVARLI_VARIANT = 'SELECT_ELVARLI_VARIANT';
export const ELVARLI_VARIANT = 'elvarliVariant';

export const SelectElvarliVariantSheet = () => {
  const showSheet = useSelector(selectShowSceneSheet);
  const isMobileLandscape = useSelector(selectIsMobileLandscape);

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const options = [
    {
      buttonLabel: translate(t.USE_POSTS),
      toggleOption: { label: translate(t.POSTS), icon: '' },
      type: ITEMS.SECTION_POSTS,
      img: './images/elvarli/posts.png',
    },
    {
      buttonLabel: translate(t.USE_SIDE_UNITS),
      toggleOption: { label: translate(t.SIDE_UNITS), icon: '' },
      type: ITEMS.SECTION_SIDE_UNITS,
      img: './images/elvarli/side_units.png',
    },
  ];

  const dispatch = useDispatch();

  /**
   * Returns user to start page
   */
  const goToStart = () => dispatch(previousView());

  /**
   * Handles selection of a variant
   */
  const selectVariant = () => {
    const selectedVariant = options[selectedIndex].type;
    localStatisticsReporter.reportSubPlannerUserChoice(selectedVariant);
    dispatch(actionSetRangeData(ELVARLI_VARIANT, selectedVariant));
    dispatch(actionHideSceneSheet());
    dispatch(actionSetFilter(constants.DEFAULT_FILTER));

    dispatch(thunkConditionalDisplayIntroPopups());
    dispatch(thunkRemoveUnwantedSectionSubFilterOptions());
  };

  /**
   * Returns toggle data
   */
  const getToggleOptions = () =>
    options.map(({ toggleOption }) => toggleOption);

  /**
   * Handle on select
   * @param selectedIndex
   */
  const _onSelect = ({ detail: { selectedIndex } }: any) =>
    setSelectedIndex(selectedIndex);

  /**
   * Render sheet header
   */
  const renderHeader = () => (
    <div>
      <KompisText
        type={ThemeFontStyleTypeEnum.headingMedium}
        theme={SkapaTheme}
      >
        {translate(t.SELECT_BASE)}
      </KompisText>
      <div className={styles.HeaderText}>
        <KompisText theme={SkapaTheme}>
          {translate(t.CHOICE_INFORMATION)}
        </KompisText>
      </div>
      <div className={styles.Toggler}>
        <KompisToggle
          items={getToggleOptions()}
          selectedIndex={selectedIndex}
          onSelect={_onSelect}
          theme={SkapaTheme}
          fluidWidth={true}
        />
      </div>
    </div>
  );

  /**
   * Render sheet body
   */
  const renderBody = () => (
    <div className={styles.Body}>
      <img
        src={options[selectedIndex].img}
        className={styles.Body__image_wrapper}
        alt=""
      />
    </div>
  );

  /**
   * Render sheet footer
   */
  const renderFooter = () => (
    <div className={styles.Footer}>
      <KompisButton
        label={options[selectedIndex].buttonLabel}
        theme={SkapaTheme}
        type={ButtonTypeEnum.primary}
        onClick={selectVariant}
        className={styles.FirstButton}
      />
      <KompisButton
        label={translate(t.BACK_TO_PRODUCT_GALLERY)}
        theme={SkapaTheme}
        invertedColors={true}
        onClick={goToStart}
        className={styles.Button}
      />
    </div>
  );

  /**
   * Render on top of each other
   */
  const renderRegular = () => (
    <div className={styles.SelectElvarliVariant}>
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </div>
  );

  /**
   * Renders body alongside rest of content
   */
  const renderLandscape = () => (
    <div className={styles.SelectElvarliVariant}>
      {renderBody()}
      <div className={styles.LandscapeContentWrapper}>
        {renderHeader()}
        {renderFooter()}
      </div>
    </div>
  );

  /**
   * Renders content based on orientation
   */
  const renderContent = () =>
    isMobileLandscape ? renderLandscape() : renderRegular();

  /**
   * Returns sheet size based on orientation
   */
  const getSheetSize = () =>
    isMobileLandscape ? SheetSizeEnum.medium : SheetSizeEnum.small;

  return (
    <KompisSheet
      visible={showSheet}
      theme={SkapaTheme}
      sheetSize={getSheetSize()}
    >
      {renderContent()}
    </KompisSheet>
  );
};
